"use client";

import { m, useReducedMotion } from "motion/react";
import { useState } from "react";

import { LandingChart } from "./landing-chart";
import {
  LandingDimensionAccordion,
  type FiveDimensionAccordionItem,
  type FiveDimensionId,
} from "./landing-dimension-accordion";

/** Figma `30535:2231` list + MCP copy for body/rows — subtitles match card tone (collapsed row). */
const DIMENSION_ITEMS: FiveDimensionAccordionItem[] = [
  {
    id: "demographic",
    title: "Demographic",
    subtitle: "4 traits verified",
    description:
      "Verified high-trust humans with multi-year history. Top-tier regional spenders with proven non-bot loyalty and consistent activity.",
    rows: [
      ["Region", "SG"],
      ["Wallet Age", "> 3 Years"],
      ["KYC Status", "Level 1 (CEX)"],
      ["Humanity Score", "> 90"],
    ],
  },
  {
    id: "assets",
    title: "Assets",
    subtitle: "5 venues verified",
    description:
      "Capital-heavy whales and yield seekers. Deep TVL providers with significant liquidity in CEX/DEX and strategic blue-chip portfolios.",
    rows: [
      ["Binance", "> $50k"],
      ["OKX", "> $25k"],
      ["Stablecoin Balance", "> $25k"],
      ["Uniswap LP", "$45k TVL"],
      ["NFT Collection Floor", "2.5 ETH"],
    ],
  },
  {
    id: "influence",
    title: "Influence",
    subtitle: "5 channels tracked",
    description:
      "Social nodes bridging Web2 reach and Web3 authority. Capable of mobilizing communities across multi-platforms to drive adoption.",
    rows: [
      ["X Blue Checkmark", "Verified"],
      ["Instagram Followers", "30k+"],
      ["Twitch", "50k+ Followers"],
      ["Reddit", "20k+ Karma"],
      ["TikTok", "100k+ Views"],
    ],
  },
  {
    id: "interests",
    title: "Interests",
    subtitle: "6 platforms active",
    description:
      "High-spending players who treat games as economies. Power users of AI with verified LTV and asset ownership.",
    rows: [
      ["ChatGPT", "Plus User"],
      ["Claude", "Pro Subscriber"],
      ["Steam", "100+ Games"],
      ["PlayStation", "Plus Premium"],
      ["Immutable/Ronin", "$20k+ Staker"],
      ["Spotify", "Premium"],
    ],
  },
  {
    id: "knowledge",
    title: "Knowledge",
    subtitle: "4 signals proven",
    description:
      "Academic elites and battle-tested builders. Top 0.1% talent with deep technical expertise, learning discipline, and hackathon success.",
    rows: [
      ["GitHub Stars", "500+"],
      ["Duolingo", "365+ Days Streak"],
      ["ETHGlobal", "Finalist"],
      ["LinkedIn Learning", "Certified"],
    ],
  },
];

/**
 * Figma `30542:9314` — row `gap-[24px]` between radar (`30967:26824`, 713×668) and accordion (`30535:2231`).
 * Avatar `299×376` centered over the `713×668` radar frame.
 */
export function LandingFiveDimensionsSection() {
  /** `null` = all collapsed; radar uses the same id, or default when nothing is open. */
  const [expandedId, setExpandedId] = useState<FiveDimensionId | null>(
    "demographic",
  );
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      id="landing-section-matching"
      aria-labelledby="landing-five-dimensions-heading"
      className="relative w-full overflow-x-clip bg-white"
    >
      <m.div
        className="relative w-full px-[10px]"
        initial={shouldReduceMotion ? "visible" : "hidden"}
        whileInView="visible"
        viewport={{ amount: 0.25, once: true }}
        variants={{
          hidden: {},
          visible: {
            transition: {
              delayChildren: 0.06,
              staggerChildren: 0.24,
            },
          },
        }}
      >
        <div className="relative w-full">
          <m.h2
            id="landing-five-dimensions-heading"
            className="pt-[58px] text-center font-ancizar font-medium text-[#080808] capitalize not-italic leading-0 tracking-[-3.6px] max-md:tracking-[-2px]"
            variants={{
              hidden: { opacity: 0, y: 28, filter: "blur(8px)" },
              visible: {
                opacity: 1,
                y: 0,
                filter: "blur(0px)",
                transition: { duration: 0.72, ease: [0.16, 1, 0.3, 1] },
              },
            }}
          >
            <span className="text-[48px] leading-[66px] max-md:text-[40px] max-md:leading-[48px]">
              Meet your ideal user
              <br aria-hidden />
              scored across five dimensions.
            </span>
          </m.h2>
          <div className="mt-[88px] flex flex-col flex-nowrap gap-12 max-lg:mt-12 lg:flex-row  lg:justify-center lg:gap-[28px]">
            <m.div
              variants={{
                hidden: { opacity: 0, x: -40, filter: "blur(8px)" },
                visible: {
                  opacity: 1,
                  x: 0,
                  filter: "blur(0px)",
                  transition: { duration: 0.78, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              <LandingChart activeId={expandedId} />
            </m.div>
            <m.div
              className="flex w-full shrink-0 flex-col gap-3 lg:w-[449px]"
              role="list"
              variants={{
                hidden: { opacity: 0, x: 40, filter: "blur(8px)" },
                visible: {
                  opacity: 1,
                  x: 0,
                  filter: "blur(0px)",
                  transition: {
                    duration: 0.72,
                    ease: [0.16, 1, 0.3, 1],
                    staggerChildren: 0.08,
                  },
                },
              }}
            >
              {DIMENSION_ITEMS.map((item) => (
                <m.div
                  key={item.id}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  <LandingDimensionAccordion
                    item={item}
                    expanded={expandedId === item.id}
                    onToggleExpanded={() => {
                      setExpandedId((cur) =>
                        cur === item.id ? null : item.id,
                      );
                    }}
                  />
                </m.div>
              ))}
            </m.div>
          </div>

          <div className="h-16 shrink-0 max-lg:h-12" aria-hidden />
        </div>
      </m.div>
    </section>
  );
}
